import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { AlertCircle, ArrowLeft, CheckCircle2, Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { AuthLayout } from '@/features/auth/components/AuthLayout';
import { useUpdatePasswordMutation } from '@/features/auth/api';
import { newPasswordSchema, type NewPasswordInput } from '@/features/auth/schema';

export function UpdatePasswordPage() {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const [showPassword, setShowPassword] = useState(false);

  const updatePwd = useUpdatePasswordMutation();

  const form = useForm<NewPasswordInput>({
    resolver: zodResolver(newPasswordSchema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  const onSubmit = (input: NewPasswordInput) => {
    if (!token) return;
    updatePwd.mutate(
      { token, password: input.password },
      {
        onSuccess: () => {
          toast.success('Password updated. Please sign in.');
          navigate('/login', { replace: true });
        },
        onError: (err) => {
          form.setError('password', {
            message: readErrorMessage(err) ?? 'Password update failed',
          });
        },
      },
    );
  };

  if (!token) {
    return (
      <AuthLayout
        title="Invalid link"
        description="This password reset link is missing its token. Please request a new one."
        footer={
          <Link to="/reset-password" className="text-primary hover:underline">
            Request a new reset link
          </Link>
        }
      >
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>The link you followed is incomplete. Tokens expire after a short period — request a fresh one.</p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Set a new password"
      description="Choose a strong password to finish updating your account."
      footer={
        <Link to="/login" className="inline-flex items-center gap-1 hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to sign in
        </Link>
      }
    >
      <Form {...form}>
        <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)} noValidate>
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>New password</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      {...field}
                      type={showPassword ? 'text' : 'password'}
                      autoFocus
                      autoComplete="new-password"
                      placeholder="••••••••"
                      disabled={updatePwd.isPending}
                      className="pr-9"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      disabled={updatePwd.isPending}
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground disabled:opacity-50"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Confirm password</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="••••••••"
                    disabled={updatePwd.isPending}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <ul className="grid grid-cols-1 gap-1 text-xs text-muted-foreground sm:grid-cols-2">
            <li className="flex items-center gap-1.5">
              <CheckCircle2 className="h-3 w-3 text-success" />
              At least 8 characters
            </li>
            <li className="flex items-center gap-1.5">
              <CheckCircle2 className="h-3 w-3 text-success" />
              At least one letter
            </li>
            <li className="flex items-center gap-1.5">
              <CheckCircle2 className="h-3 w-3 text-success" />
              At least one number
            </li>
            <li className="flex items-center gap-1.5">
              <CheckCircle2 className="h-3 w-3 text-success" />
              Matches confirmation
            </li>
          </ul>

          {updatePwd.isError && !form.formState.errors.password && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{readErrorMessage(updatePwd.error) ?? 'Password update failed'}</p>
            </div>
          )}

          <Button type="submit" className="w-full" disabled={updatePwd.isPending}>
            {updatePwd.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {updatePwd.isPending ? 'Updating…' : 'Update password'}
          </Button>
        </form>
      </Form>
    </AuthLayout>
  );
}

function readErrorMessage(err: unknown): string | undefined {
  if (!err) return undefined;
  if (err instanceof Error) return err.message;
  if (typeof err === 'object' && err !== null) {
    const maybeAxios = err as { response?: { data?: { message?: string } }; message?: string };
    return maybeAxios.response?.data?.message ?? maybeAxios.message;
  }
  if (typeof err === 'string') return err;
  return undefined;
}
